import React, { useState, useEffect, useCallback } from "react";
import API_BASE_URL from "../RestAPI";
import {
  FiSearch, FiFilter, FiChevronLeft, FiChevronRight, FiX, FiCopy, FiCheck,
  FiGitBranch, FiExternalLink, FiRefreshCw, FiChevronUp, FiChevronDown, FiZap,
} from "react-icons/fi";
import "./ErrorList.css";
import "./AIFixPanel.css";
import AIFixPanel from "./AIFixPanel";
import "./AIFixList.css";

/* Same palette as the error list so a stage means the same thing on both pages. */
const STAGE_OPTIONS = [
  { value: "not_yet_taken", label: "Not Yet Taken", color: "#9e9e9e" },
  { value: "in_dev", label: "In Dev", color: "#2196f3" },
  { value: "in_qa", label: "In QA", color: "#ff9800" },
  { value: "qa_fail", label: "QA Fail", color: "#f44336" },
  { value: "in_uat", label: "In UAT", color: "#9c27b0" },
  { value: "production", label: "Production", color: "#4caf50" },
];

const getStageInfo = (stage) =>
  STAGE_OPTIONS.find((s) => s.value === stage) || STAGE_OPTIONS[0];

const STATUS_TONE = {
  queued: "info", analyzing: "info", patching: "info", verifying: "info",
  branch_ready: "success", needs_review: "warn", no_code_fix: "warn",
  failed: "danger", rejected: "danger", cancelled: "muted",
};

const CONFIDENCE_TONE = { high: "success", medium: "warn", low: "danger" };

const EMPTY_FILTERS = {
  date_from: "", date_to: "", status: "", stage: "", module: "", severity: "",
  organization: "", confidence: "", verdict: "", pushed: "", mode: "",
  requested_by: "", search: "",
};

/* Seconds -> "1m 36s", which is how people actually talk about these runs. */
const fmtDuration = (secs) => {
  if (!secs) return "-";
  const m = Math.floor(secs / 60);
  const s = secs % 60;
  return m ? `${m}m ${s}s` : `${s}s`;
};

const fmtTimestamp = (ts) => {
  if (!ts) return "-";
  return new Date(ts).toLocaleString("en-IN", {
    day: "2-digit", month: "short", year: "numeric",
    hour: "2-digit", minute: "2-digit",
  });
};

const AIFixList = () => {
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState(null);
  const [pagination, setPagination] = useState({ page: 1, page_size: 25, total_count: 0, total_pages: 0 });
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [filterOptions, setFilterOptions] = useState(null);
  const [showFilters, setShowFilters] = useState(false);
  const [loading, setLoading] = useState(true);
  const [sortBy, setSortBy] = useState("created_at");
  const [sortOrder, setSortOrder] = useState("desc");
  const [copied, setCopied] = useState("");
  const [selected, setSelected] = useState(null);

  const headers = {
    "Content-Type": "application/json",
    Authorization: `Bearer ${localStorage.getItem("authToken")}`,
  };

  const fetchRows = useCallback(async (page = 1) => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.append("page", page);
      params.append("page_size", pagination.page_size);
      params.append("sort_by", sortBy);
      params.append("sort_order", sortOrder);
      Object.entries(filters).forEach(([k, val]) => {
        if (val) params.append(k, val);
      });
      const res = await fetch(`${API_BASE_URL}error-tracking/ai-fix/report/?${params}`, { headers });
      const data = await res.json();
      if (data.status === "success") {
        setRows(data.data || []);
        setSummary(data.summary || null);
        setPagination(data.pagination || {});
      }
    } catch (err) {
      console.error("AI fix report error:", err);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, pagination.page_size, sortBy, sortOrder]);

  const fetchFilterOptions = useCallback(async () => {
    try {
      const res = await fetch(`${API_BASE_URL}error-tracking/ai-fix/report/filters/`, { headers });
      const data = await res.json();
      if (data.status === "success") setFilterOptions(data.data);
    } catch (err) {
      console.error("AI fix filters error:", err);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    fetchRows(1);
    fetchFilterOptions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    fetchRows(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sortBy, sortOrder]);

  // The panel fetches the full record itself, so opening is just "which row".
  const openDetail = (row) => setSelected(row);

  const updateStage = async (fingerprint, batchNumber, newStage) => {
    try {
      const res = await fetch(`${API_BASE_URL}error-tracking/errors/update-stage/`, {
        method: "PATCH",
        headers,
        body: JSON.stringify({
          error_fingerprint: fingerprint,
          batch_number: batchNumber,
          stage: newStage,
        }),
      });
      const data = await res.json();
      if (data.status === "success") fetchRows(pagination.page);
    } catch (err) {
      console.error("Stage update error:", err);
    }
  };

  const copy = (text, key) => {
    navigator.clipboard.writeText(text);
    setCopied(key);
    setTimeout(() => setCopied(""), 1500);
  };

  const handleSort = (field) => {
    if (sortBy === field) setSortOrder((p) => (p === "desc" ? "asc" : "desc"));
    else { setSortBy(field); setSortOrder("desc"); }
  };

  const setFilter = (key, value) => setFilters((p) => ({ ...p, [key]: value }));
  const clearFilters = () => { setFilters(EMPTY_FILTERS); };

  useEffect(() => {
    if (filters === EMPTY_FILTERS) fetchRows(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters]);

  const sortIcon = (field) =>
    sortBy === field ? (sortOrder === "desc" ? <FiChevronDown size={12} /> : <FiChevronUp size={12} />) : null;

  return (
    <div className="el-page">
      {/* Header */}
      <div className="el-header">
        <h2 className="el-header__title">AI Fixes</h2>
        <div className="el-header__actions">
          <div className="el-search-box">
            <FiSearch size={14} />
            <input
              type="text"
              placeholder="Search branch, root cause, URL, commit..."
              value={filters.search}
              onChange={(e) => setFilter("search", e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && fetchRows(1)}
            />
          </div>
          <button
            className={`el-btn el-btn--outline ${showFilters ? "el-btn--active" : ""}`}
            onClick={() => setShowFilters((v) => !v)}
          >
            <FiFilter size={13} /> Filters
          </button>
          <button className="el-btn el-btn--primary" onClick={() => fetchRows(pagination.page)}>
            <FiRefreshCw size={13} /> Refresh
          </button>
        </div>
      </div>

      {/* Summary for the chosen filter */}
      {summary && (
        <div className="af-cards">
          <div className="af-card af-card--accent">
            <span className="af-card__label">AI used</span>
            <span className="af-card__value">{summary.ai_runs}</span>
            <span className="af-card__sub">times in this filter</span>
          </div>
          <div className="af-card">
            <span className="af-card__label">AI time</span>
            <span className="af-card__value">{summary.ai_total_minutes}<small>min</small></span>
            <span className="af-card__sub">{summary.ai_total_turns} turns total</span>
          </div>
          <div className="af-card">
            <span className="af-card__label">Avg per fix</span>
            <span className="af-card__value">{fmtDuration(summary.ai_avg_seconds)}</span>
            <span className="af-card__sub">wall clock</span>
          </div>
          <div className="af-card">
            <span className="af-card__label">Branches</span>
            <span className="af-card__value">{summary.branches_created}</span>
            <span className="af-card__sub">{summary.pushed} pushed</span>
          </div>
          <div className="af-card af-card--success">
            <span className="af-card__label">In production</span>
            <span className="af-card__value">{summary.in_production}</span>
            <span className="af-card__sub">reached live</span>
          </div>
          <div className="af-card">
            <span className="af-card__label">Pending review</span>
            <span className="af-card__value">{summary.pending_review}</span>
            <span className="af-card__sub">{summary.accepted} accepted / {summary.rejected} rejected</span>
          </div>
          <div className="af-card">
            <span className="af-card__label">Accept rate</span>
            <span className="af-card__value">{summary.accept_rate_pct}<small>%</small></span>
            <span className="af-card__sub">of reviewed fixes</span>
          </div>
          <div className="af-card af-card--muted">
            <span className="af-card__label">Not a code bug</span>
            <span className="af-card__value">{summary.no_code_fix}</span>
            <span className="af-card__sub">{summary.failed} failed</span>
          </div>
        </div>
      )}

      {/* Filters */}
      {showFilters && (
        <div className="el-filters">
          <div className="el-filters__grid">
            <div className="el-filter-group">
              <label>From</label>
              <input type="date" value={filters.date_from} onChange={(e) => setFilter("date_from", e.target.value)} />
            </div>
            <div className="el-filter-group">
              <label>To</label>
              <input type="date" value={filters.date_to} onChange={(e) => setFilter("date_to", e.target.value)} />
            </div>
            <div className="el-filter-group">
              <label>Job Status</label>
              <select value={filters.status} onChange={(e) => setFilter("status", e.target.value)}>
                <option value="">All</option>
                {(filterOptions?.statuses || []).map((s) => (
                  <option key={s.value} value={s.value}>{s.label}</option>
                ))}
              </select>
            </div>
            <div className="el-filter-group">
              <label>Stage</label>
              <select value={filters.stage} onChange={(e) => setFilter("stage", e.target.value)}>
                <option value="">All</option>
                {(filterOptions?.stages || STAGE_OPTIONS).map((s) => (
                  <option key={s.value} value={s.value}>{s.label}</option>
                ))}
              </select>
            </div>
            <div className="el-filter-group">
              <label>Module</label>
              <select value={filters.module} onChange={(e) => setFilter("module", e.target.value)}>
                <option value="">All</option>
                {(filterOptions?.modules || []).map((m) => <option key={m} value={m}>{m}</option>)}
              </select>
            </div>
            <div className="el-filter-group">
              <label>Severity</label>
              <select value={filters.severity} onChange={(e) => setFilter("severity", e.target.value)}>
                <option value="">All</option>
                {(filterOptions?.severities || []).map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
            <div className="el-filter-group">
              <label>Organization</label>
              <select value={filters.organization} onChange={(e) => setFilter("organization", e.target.value)}>
                <option value="">All</option>
                {(filterOptions?.organizations || []).map((o) => <option key={o} value={o}>{o}</option>)}
              </select>
            </div>
            <div className="el-filter-group">
              <label>Confidence</label>
              <select value={filters.confidence} onChange={(e) => setFilter("confidence", e.target.value)}>
                <option value="">All</option>
                <option value="high">High</option>
                <option value="medium">Medium</option>
                <option value="low">Low</option>
              </select>
            </div>
            <div className="el-filter-group">
              <label>Review</label>
              <select value={filters.verdict} onChange={(e) => setFilter("verdict", e.target.value)}>
                <option value="">All</option>
                <option value="pending">Pending</option>
                <option value="accepted">Accepted</option>
                <option value="rejected">Rejected</option>
              </select>
            </div>
            <div className="el-filter-group">
              <label>Pushed</label>
              <select value={filters.pushed} onChange={(e) => setFilter("pushed", e.target.value)}>
                <option value="">All</option>
                <option value="true">Pushed</option>
                <option value="false">Not pushed</option>
              </select>
            </div>
            <div className="el-filter-group">
              <label>Mode</label>
              <select value={filters.mode} onChange={(e) => setFilter("mode", e.target.value)}>
                <option value="">All</option>
                {(filterOptions?.modes || []).map((m) => (
                  <option key={m.value} value={m.value}>{m.label}</option>
                ))}
              </select>
            </div>
            <div className="el-filter-group">
              <label>Requested by</label>
              <select value={filters.requested_by} onChange={(e) => setFilter("requested_by", e.target.value)}>
                <option value="">All</option>
                {(filterOptions?.requesters || []).map((rq) => <option key={rq} value={rq}>{rq}</option>)}
              </select>
            </div>
          </div>
          <div className="el-filters__actions">
            <button className="el-btn el-btn--ghost" onClick={clearFilters}>Clear All</button>
            <button className="el-btn el-btn--primary" onClick={() => fetchRows(1)}>Apply</button>
          </div>
        </div>
      )}

      {/* Table */}
      <div className="el-table-wrap">
        {loading ? (
          <div className="el-loading"><div className="el-loading__spinner" /><span>Loading AI fixes...</span></div>
        ) : rows.length === 0 ? (
          <div className="el-empty">
            No AI fixes yet. Open an error and click <strong>Fix with AI</strong> to create one.
          </div>
        ) : (
          <table className="el-table">
            <thead>
              <tr>
                <th className="el-th--sortable" onClick={() => handleSort("created_at")}>
                  Created {sortIcon("created_at")}
                </th>
                <th>Error</th>
                <th>Module</th>
                <th>Branch</th>
                <th className="el-th--sortable" onClick={() => handleSort("status")}>
                  Status {sortIcon("status")}
                </th>
                <th className="el-th--sortable" onClick={() => handleSort("stage")}>
                  Stage {sortIcon("stage")}
                </th>
                <th className="el-th--sortable" onClick={() => handleSort("confidence")}>
                  Confidence {sortIcon("confidence")}
                </th>
                <th className="el-th--sortable" onClick={() => handleSort("duration_seconds")}>
                  AI time {sortIcon("duration_seconds")}
                </th>
                <th>Review</th>
                <th>Requested by</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => {
                const stageInfo = getStageInfo(row.stage);
                const tone = STATUS_TONE[row.status] || "muted";
                return (
                  <tr key={row.job_id} className={`el-row ${row.in_production ? "af-row--prod" : ""}`}>
                    <td className="el-cell--time">{fmtTimestamp(row.created_at)}</td>
                    <td className="el-cell--url" title={row.error_url}>
                      <span className={`et-method et-method--${row.error_method?.toLowerCase()}`}>
                        {row.error_method}
                      </span>{" "}
                      {row.error_url?.length > 38 ? row.error_url.slice(0, 38) + "..." : row.error_url}
                      {row.error_status_code ? (
                        <span className="et-status-badge af-status-inline">{row.error_status_code}</span>
                      ) : null}
                    </td>
                    <td>{row.error_module || "-"}</td>
                    <td className="af-cell--branch">
                      {row.branch_name ? (
                        <div className="af-branch">
                          <FiGitBranch size={11} />
                          <code title={row.branch_name}>{row.branch_name}</code>
                          <button
                            className="el-copy-btn"
                            title="Copy branch name"
                            onClick={() => copy(row.branch_name, `b${row.job_id}`)}
                          >
                            {copied === `b${row.job_id}` ? <FiCheck size={10} /> : <FiCopy size={10} />}
                          </button>
                          <button
                            className="el-copy-btn"
                            title={`Copy: ${row.checkout_command}`}
                            onClick={() => copy(row.checkout_command, `c${row.job_id}`)}
                          >
                            {copied === `c${row.job_id}` ? <FiCheck size={10} /> : <span className="af-cmd">git</span>}
                          </button>
                          {row.branch_url && (
                            <a href={row.branch_url} target="_blank" rel="noreferrer" title="View on GitLab">
                              <FiExternalLink size={10} />
                            </a>
                          )}
                        </div>
                      ) : (
                        <span className="el-aifix-dash">-</span>
                      )}
                      {row.branch_name && (
                        <span className={`af-push ${row.pushed ? "af-push--yes" : "af-push--no"}`}>
                          {row.pushed ? "pushed" : "local only"}
                        </span>
                      )}
                    </td>
                    <td>
                      <span className={`aifix-status aifix-status--${tone}`}>{row.status_label}</span>
                    </td>
                    <td>
                      <select
                        className="el-stage-select"
                        value={row.stage}
                        style={{ borderColor: stageInfo.color, color: stageInfo.color }}
                        onChange={(e) => updateStage(row.error_fingerprint, row.batch_number, e.target.value)}
                      >
                        {STAGE_OPTIONS.map((s) => (
                          <option key={s.value} value={s.value}>{s.label}</option>
                        ))}
                      </select>
                    </td>
                    <td>
                      {row.confidence ? (
                        <span className={`aifix-chip aifix-chip--${CONFIDENCE_TONE[row.confidence] || "info"}`}>
                          {row.confidence}
                        </span>
                      ) : "-"}
                      {row.verify_passed === false && (
                        <span className="aifix-chip aifix-chip--danger">checks failed</span>
                      )}
                    </td>
                    <td className="el-cell--count">{fmtDuration(row.duration_seconds)}</td>
                    <td>
                      {row.review_verdict ? (
                        <span className={`aifix-chip aifix-chip--${row.review_verdict === "accepted" ? "success" : "danger"}`}>
                          {row.review_verdict}
                        </span>
                      ) : (
                        <span className="el-aifix-dash">pending</span>
                      )}
                    </td>
                    <td className="el-cell--user" title={row.requested_by_name}>
                      {row.requested_by_name || "-"}
                    </td>
                    <td>
                      <button className="el-action-btn" title="View detail" onClick={() => openDetail(row)}>
                        <FiExternalLink size={13} />
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {/* Pagination */}
      {pagination.total_pages > 1 && (
        <div className="el-pagination">
          <span className="el-pagination__info">
            Page {pagination.page} of {pagination.total_pages} &middot; {pagination.total_count} fix(es)
          </span>
          <div className="el-pagination__controls">
            <button
              className="el-page-btn"
              disabled={pagination.page <= 1}
              onClick={() => fetchRows(pagination.page - 1)}
            ><FiChevronLeft size={14} /></button>
            <button
              className="el-page-btn"
              disabled={pagination.page >= pagination.total_pages}
              onClick={() => fetchRows(pagination.page + 1)}
            ><FiChevronRight size={14} /></button>
          </div>
        </div>
      )}

      {/* Detail modal - reuses AIFixPanel so questions, answers, accept and
          reject behave exactly as they do on the Error List. `jobId` pins it to
          the row that was clicked rather than the newest job for that pattern. */}
      {selected && (
        <div className="el-modal-overlay" onClick={() => setSelected(null)}>
          <div className="el-modal" onClick={(e) => e.stopPropagation()}>
            <div className="el-modal__header">
              <h3><FiZap size={14} /> AI Fix #{selected.job_id}</h3>
              <button className="el-modal__close" onClick={() => setSelected(null)}><FiX size={18} /></button>
            </div>
            <div className="el-modal__body">
              <AIFixPanel
                jobId={selected.job_id}
                error={{
                  id: selected.error_id,
                  error_fingerprint: selected.error_fingerprint,
                  batch_number: selected.batch_number,
                }}
                onJobChange={() => fetchRows(pagination.page)}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AIFixList;
